import { Component, type ErrorInfo, type ReactNode } from 'react'

type Props = {
  /** Shown instead of the part that failed to draw. */
  fallback: ReactNode
  /** When this changes — another lesson, another tab — the part gets another chance. */
  resetKey?: unknown
  children: ReactNode
}

/**
 * Keeps one broken part of a page from blanking the whole page.
 *
 * React unmounts everything above a render error that nothing catches. That
 * is how a single feature an older iPhone did not support once turned a
 * lesson into a white screen. Wrapped around each exercise and each piece of
 * lesson text, a failure stays local and says so quietly; the error still
 * reaches the console for whoever debugs it.
 */
export class PartBoundary extends Component<Props, { failed: boolean }> {
  override state = { failed: false }

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true }
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    console.error(error, info.componentStack)
  }

  override componentDidUpdate(prev: Props): void {
    if (this.state.failed && prev.resetKey !== this.props.resetKey) this.setState({ failed: false })
  }

  override render(): ReactNode {
    return this.state.failed ? this.props.fallback : this.props.children
  }
}
